import { UploadWindowView } from '../view/UploadWindowView';
import { templateUploadWindow } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig
} from '../../../framework/BaseWebComponentController';
import { UploadWindowWebComponent } from '..';
import {
  FILE_ADDED,
  FILE_REMOVED,
  START_VALIDATE_PATH,
  ValidateDownloadPathErrorCodes,
  UploadWindowWebComponentSchema,
  VALIDATE_PATH,
  MAX_FILES_NUMBER,
} from '../types';
import { GzInputFileWebComponent } from '../../../ui/gzInputFile';
import { GzChipItemWebComponent } from '../../../ui/gzChips';
import { elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { convertUnits } from '../utils';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { translateFn } from '../../../utils/translator.helper';

export class UploadWindowController extends BaseWebComponentController {
  public component: UploadWindowWebComponent;
  public view: UploadWindowView;
  public viewConstructor: typeof UploadWindowView;

  constructor(component: UploadWindowWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: UploadWindowView,
      viewTemplate: templateUploadWindow,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.filesInput,
        event: 'change',
        callback: this.handleBrowseInput,
      },
      {
        element: this.view.dom.$pathInput,
        event: 'blur',
        callback: this.onPathBlur,
      },
      {
        element: this.view.dom.$pathInput,
        event: 'change',
        callback: this.onPathChange,
      },
    ];
  }

  async getDataForView(): Promise<UploadWindowWebComponentSchema> {
    return {
      path: this.component.path,
      filesSize: this.component.filesSize,
      streamId: this.component.streamId,
      filesNumber: this.component.filesNumber,
      maxFilesSize: this.component.maxFilesSize,
    };
  }

  handleBrowseInput() {
    const files = this.getFiles();

    if (!files) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!this.shouldAddFile(file)) {
        continue;
      }

      this.component.files.push(file);

      this.view.dom.$wrapper.dispatchEvent(
        new CustomEvent(FILE_ADDED, {
          bubbles: true,
          detail: {
            file,
          },
          cancelable: true,
          composed: true,
        })
      );
    }

    this.updateFilesList();
  }

  shouldAddFile(file) {
    return !this.component.files.find(item => {
      return (
        item.name === file.name &&
        item.size === file.size &&
        item.type === file.type &&
        item.lastModified === file.lastModified
      );
    });
  }

  updateFilesList() {
    this.view.dom.$filesList.innerHTML = '';

    let totalSize = 0;
    const wrapper = document.createElement('div');

    const files = this.component.files;
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const chip = new GzChipItemWebComponent();
      const container = document.createElement('div');
      elementClassListAdd(container, 'chip-container');

      chip.text = `${file.name}`;
      chip.notSelectable = true;
      chip.removable = true;
      chip.addEventListener('removeChip', () => {
        this.component.files.splice(index, 1);
        this.updateFilesList();
        this.view.dom.$wrapper.dispatchEvent(
          new CustomEvent(FILE_REMOVED, {
            bubbles: true,
            detail: {
              file,
            },
            cancelable: true,
            composed: true,
          })
        );

        this.view.dom.filesInput.setFormValue(null);
      });

      const size = new GzTitleWebComponent();
      size.textContent = convertUnits(file.size);
      size.fontSize = FontSizes.PARAGRAPH_11;
      size.fontWeight = FontWeights.SEMIBOLD;
      if (file.size === 0) {
        elementClassListAdd(size, 'error');
      }

      container.appendChild(chip);
      container.appendChild(size);

      const i = files.findIndex(item => file.name === item.name);
      if (i !== index) {
        const duplicate = new GzTitleWebComponent();
        duplicate.textContent = translateFn('Duplicate');
        duplicate.fontSize = FontSizes.PARAGRAPH_11;
        duplicate.fontWeight = FontWeights.SEMIBOLD;
        elementClassListAdd(duplicate, 'duplicate');
        container.appendChild(duplicate);
      }

      wrapper.appendChild(container);

      this.view.dom.$filesList.appendChild(wrapper);

      totalSize += file.size;
    }

    this.view.dom.$filesSize.textContent = `${convertUnits(totalSize)} / ${convertUnits(this.component.maxFilesSize)}`;
    if (totalSize > this.component.maxFilesSize) {
      elementClassListAdd(this.view.dom.$filesTitleContainer, 'error');
    } else {
      elementClassListRemove(this.view.dom.$filesTitleContainer, 'error');
    }

    this.view.dom.$filesNumber.textContent = `${files.length} / ${MAX_FILES_NUMBER}`;
    if (files.length > MAX_FILES_NUMBER) {
      elementClassListAdd(this.view.dom.$filesNumberTitleContainer, 'error');
    } else {
      elementClassListRemove(this.view.dom.$filesNumberTitleContainer, 'error');
    }
  }

  getFiles(): FileList | null {
    const input = this.view.dom.filesInput as GzInputFileWebComponent;

    if (!input) {
      return null;
    }

    const files = input.getFormValue();

    if (!files || !files.length) {
      return null;
    }

    return files;
  }

  async onPathBlur() {
    await this.validateUploadPath();
  }

  onPathChange() {
    this.component.path = this.view.dom.$pathInput.getFormValue();
  }

  public async validateUploadPath() {
    this.view.dom.$uploadPathField.markAsValid();
    this.view.dom.$uploadPathField.touched = true;
    let valid = await this.view.dom.$uploadPathField.dryValidate();

    let code;
    try {
      if (valid) {
        this.setPathReadOnly(true);
        this.dispatchEvent(START_VALIDATE_PATH, { valid });

        const response = await this.dependencies.api.clients.RemoteShell.makeRequest(
          'gzConsoleRemoteShell/validateUploadPath',
          {
            path: this.view.dom.$pathInput.getFormValue(),
            streamId: this.component.streamId,
          }
        ).toPromise();

        valid = valid && response.valid;
        code = response.errorCode;
      }
    } catch (err) {
      console.error(err);
      valid = false;
      code = -1;
    }

    if (!this.view.dom.$uploadPathField) {
      return;
    }

    if (!valid) {
      this.view.dom.$uploadPathField.markAsInvalid(
        !code ? translateFn('Required field') : this.getMessageFromCode(code)
      );
    }

    this.setPathReadOnly(false);
    this.dispatchEvent(VALIDATE_PATH, { valid });
  }

  setPathReadOnly(value: boolean) {
    if (this.view.dom.$uploadPathField?.Input) {
      this.view.dom.$uploadPathField.Input.readonly = value;
    }
  }

  dispatchEvent(name, detail = {}) {
    this.view.dom.$wrapper.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail,
      })
    );
  }

  getMessageFromCode(code) {
    switch (code) {
      case ValidateDownloadPathErrorCodes.PATH_NOT_FOUND:
        return translateFn('Invalid file path.');
      case ValidateDownloadPathErrorCodes.NOT_DIRECTORY:
        return translateFn('The specified path is not a folder.');
      case ValidateDownloadPathErrorCodes.ACCESS_DENIED:
        return translateFn('Insufficient permissions. Choose a different folder.');
      case ValidateDownloadPathErrorCodes.ACCESS_DENIED_BEST_DIR:
        return translateFn('The specified path is inside the BEST installation folder. Choose a different folder.');
      case ValidateDownloadPathErrorCodes.PATH_EMPTY:
        return translateFn('No file path was specified.');
      case ValidateDownloadPathErrorCodes.NOT_ENOUGH_MEMORY:
        return translateFn('Insufficient memory or disk space');
      case ValidateDownloadPathErrorCodes.INVALID_NAME:
        return translateFn('No file path was specified, or the file path contains unsupported characters.');
      case ValidateDownloadPathErrorCodes.RELATIVE_PATH:
        return translateFn('Relative file path. Please provide the full file path.');
      case ValidateDownloadPathErrorCodes.BAD_PROFILE:
      case ValidateDownloadPathErrorCodes.NOT_IMPLEMENTED:
      default:
        return translateFn('Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.', {
          ERROR_CODE: code,
        });
    }
  }
}
